import { Fragment, ReactNode } from 'react';

import { ListingTile } from '@nocowanie/noc-api';

import { IconsLinear } from '@nocowanie/icons/linear';
import { Clock2, MessageQuestion } from '@nocowanie/icons/two-tone';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile';
import { ACCOMMODATION_TILE } from '@app/consts';

import s from './accommodation-tile-lite.module.css';
import { TileIcon } from './tile-icon';

interface BadgeConfig {
    id: string;
    testId: string;
    className: string;
    icon: ReactNode;
    text: string;
    tooltip?: { text: string; className: string };
}

interface TileBadgesProps {
    source?: ListingTile['source'];
    reservationType?: string;
    isBestseller?: boolean;
    listingPlacement?: string;
    t: AccommodationTileProps['t'];
}

/** Same badges and markup as `AccommodationTileBadges`, without Chakra runtime styling */
export const TileBadges = ({
    source,
    reservationType,
    isBestseller,
    listingPlacement,
    t,
}: TileBadgesProps) => {
    const isInquiry = source === ListingTile.source.NOC;
    const isRealTime = !isInquiry && reservationType === 'RealTime';
    const badgeConfigs: BadgeConfig[] = [];

    if (isInquiry) {
        badgeConfigs.push({
            id: 'inquiry',
            testId: 'acc-tile-badge-inquiry',
            className: s.badgeInquiry,
            icon: <TileIcon icon={MessageQuestion} className={s.badgeIconOrange} />,
            text: t('badges.inquiry'),
            tooltip: { text: t('badges.tooltips.inquiry'), className: s.tooltipInquiry },
        });
    } else if (isRealTime) {
        badgeConfigs.push({
            id: 'realtime',
            testId: 'acc-tile-badge-realtime',
            className: s.badgeRealtime,
            icon: <TileIcon icon={IconsLinear.Flash} className={s.badgeIconFlash} />,
            text: t('badges.realtime'),
            tooltip: { text: t('badges.tooltips.realtime'), className: s.tooltipRealtime },
        });
    } else {
        badgeConfigs.push({
            id: 'onRequest',
            testId: 'acc-tile-badge-on-request',
            className: s.badgeOnRequest,
            icon: <TileIcon icon={Clock2} className={s.badgeIconOrange} />,
            text: t('badges.onRequest'),
            tooltip: { text: t('badges.tooltips.onRequest'), className: s.tooltipOnRequest },
        });
    }

    if (isBestseller) {
        badgeConfigs.push({
            id: 'bestseller',
            testId: 'acc-tile-badge-bestseller',
            className: s.badgeOutline,
            icon: <TileIcon icon={IconsLinear.Like1} className={s.badgeIconAccent} />,
            text: t('badges.bestseller'),
        });
    }

    if (isInquiry && listingPlacement === 'Guarantee') {
        badgeConfigs.push({
            id: 'promoted',
            testId: 'acc-tile-badge-promoted',
            className: s.badgeOutline,
            icon: <TileIcon icon={IconsLinear.Star} className={s.badgeIconOrange} />,
            text: t('badges.promoted'),
        });
    }

    const badges = badgeConfigs.map(({ id, testId, className, icon, text, tooltip }) => {
        const badge = (
            <span data-testid={testId} className={className}>
                {icon}
                {text}
            </span>
        );

        return tooltip ? (
            <Fragment key={id}>
                <div className={s.badgeTooltipRoot}>
                    {badge}
                    <div
                        className={`${s.tooltipContent} ${tooltip.className}`}
                        data-testid={'tooltip-content'}
                    >
                        {tooltip.text}
                    </div>
                </div>
                <div className={s.badgeMobile}>{badge}</div>
            </Fragment>
        ) : (
            <div key={id}>{badge}</div>
        );
    });

    return (
        <div className={s.badges} data-testid={'acc-tile-badges'}>
            {badges.slice(0, ACCOMMODATION_TILE.MAX_MOBILE_BADGES)}
            {badges.slice(ACCOMMODATION_TILE.MAX_MOBILE_BADGES).map(badge => (
                <span key={badge.key} className={s.hideBelowMd}>
                    {badge}
                </span>
            ))}
        </div>
    );
};
